import { type ChangeEvent, type FC } from 'react'
import { type IntlShape, useIntl } from 'react-intl'

import { FormHelperText } from 'base-ui'

import { RadioInput } from '../../base-form-handling'
import { useGlobalConfigContext } from '../../global-config'
import { TENANT } from '../../tenant'

interface RadioParams {
  dataQaId: string
  id: string
  label: string
  value: string
}

const TENANTS_WITH_DIVERSE_UNSPECIFIED_SALUTATIONS = new Set<TENANT>([
  TENANT.AT,
  TENANT.COM,
  TENANT.IT,
])

const getSalutations = (tenant: TENANT, intl: IntlShape): RadioParams[] => {
  const salutations: RadioParams[] = [
    {
      dataQaId: 'salutation-female',
      id: 'salutation_female',
      label: intl.formatMessage({
        id: 'general.label.salutation.mrs',
      }),
      value: 'mrs',
    },
    {
      dataQaId: 'salutation-male',
      id: 'salutation_male',
      label: intl.formatMessage({ id: 'general.label.salutation.mr' }),
      value: 'mr',
    },
  ]
  if (TENANTS_WITH_DIVERSE_UNSPECIFIED_SALUTATIONS.has(tenant)) {
    salutations.push(
      {
        dataQaId: 'salutation-diverse',
        id: 'salutation_diverse',
        label: intl.formatMessage({ id: 'general.label.salutation.diverse' }),
        value: 'diverse',
      },
      {
        dataQaId: 'salutation-unspecified',
        id: 'salutation_unspecified',
        label: intl.formatMessage({
          id: 'general.label.salutation.unspecified',
        }),
        value: 'unspecified',
      },
    )
  }

  return salutations
}

interface SalutationProps {
  /**
   * Prefix for data-qa-id attributes
   */
  dataQaIdPrefix?: string
  /**
   * Whether the salutation field is disabled
   */
  disabled?: boolean
  /**
   * Error message to display
   */
  error?: string
  /**
   * Name attribute for the radio group
   */
  name?: string
  /**
   * Callback when salutation changes
   */
  onChange?: (value: string) => void
  /**
   * Whether the field is required (shows asterisk)
   */
  required?: boolean
  /**
   * The currently selected salutation value
   */
  value: string
}

export const Salutation: FC<SalutationProps> = ({
  dataQaIdPrefix = '',
  disabled = false,
  error,
  name = 'salutation',
  onChange,
  required = false,
  value,
}) => {
  const intl = useIntl()
  const { tenant } = useGlobalConfigContext()
  const salutationRadios = getSalutations(tenant, intl)

  return (
    <div className="mb-1 flex flex-col">
      <fieldset className="flex flex-row flex-wrap gap-5" disabled={disabled}>
        {salutationRadios.map((entry) => {
          const dataQaId = dataQaIdPrefix
            ? `${dataQaIdPrefix}-${entry.dataQaId}`
            : entry.dataQaId

          return (
            <RadioInput
              checked={value === entry.value}
              dataQaId={dataQaId}
              id={entry.id}
              key={entry.id}
              label={`${entry.label}${required ? '*' : ''}`}
              name={name}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                if (onChange) {
                  onChange(event.target.value)
                }
              }}
              size="small"
              type="radio"
              value={entry.value}
            />
          )
        })}
      </fieldset>
      {error ? <FormHelperText error>{error}</FormHelperText> : null}
    </div>
  )
}
